import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, catchError, of } from 'rxjs';
import { utils } from '../helper/utils';
import { CodeDescription } from '../models/code-description.model';
import { AuditLog } from '../models/audit-log.model';
import { DownloadRequest } from '../models/download-filter.model';
import { RepairLocationListItem } from '../models/repair-location-list-item.model';
import { RepairLocationFilter, RepairLocationFilterFormValue } from '../models/repair-location-filter.model';
import { CreateRepairLocationRequest, UpdateRepairLocationRequest } from '../models/create-repair-location-request.model';

@Injectable({ providedIn: 'root' })
export class RepairLocationService {
  constructor(private readonly httpClient: HttpClient) {}

  getRepairLocationList(
    page: number,
    pageLimit: number,
    sort?: string,
    filters?: RepairLocationFilter,
  ): Observable<HttpResponse<RepairLocationListItem[]>> {
    const params: Record<string, string | number | string[]> = {
      page,
      limit: pageLimit > 0 ? pageLimit : 100,
    };
    if (sort) params['sort'] = sort;
    if (filters?.repairLocationCodeList?.length) params['repairLocationCodeDescriptionList'] = filters.repairLocationCodeList.join(',');
    if (filters?.SearchText) params['SearchText'] = filters.SearchText;

    const url = utils.GetMasterDataManagementUrl('repair-location');
    return this.httpClient
      .get<RepairLocationListItem[]>(url, { params, observe: 'response' })
      .pipe(catchError(() => of(new HttpResponse<RepairLocationListItem[]>({ body: [], headers: undefined }))));
  }

  getRepairLocationByCode(code: string): Observable<RepairLocationListItem | null> {
    const url = utils.GetMasterDataManagementUrl(`repair-location/${encodeURIComponent(code)}`);
    return this.httpClient.get<RepairLocationListItem>(url).pipe(catchError(() => of(null)));
  }

  getAllCodeDescriptions(): Observable<CodeDescription[]> {
    const url = utils.GetMasterDataManagementUrl('repair-location/code-description/all');
    return this.httpClient.get<CodeDescription[]>(url).pipe(catchError(() => of([])));
  }

  createRepairLocation(request: CreateRepairLocationRequest): Observable<RepairLocationListItem> {
    const url = utils.GetMasterDataManagementUrl('repair-location');
    return this.httpClient.post<RepairLocationListItem>(url, request);
  }

  updateRepairLocation(code: string, request: UpdateRepairLocationRequest): Observable<RepairLocationListItem> {
    const url = utils.GetMasterDataManagementUrl(`repair-location/${encodeURIComponent(code)}`);
    return this.httpClient.put<RepairLocationListItem>(url, request);
  }

  getAuditLogs(
    code: string,
    isSystemLog: boolean = false,
    page: number = 1,
    limit: number = 100,
  ): Observable<HttpResponse<AuditLog[]>> {
    const path = `repair-location/${encodeURIComponent(code)}/audit-logs?isSystemLog=${isSystemLog}&page=${page}&limit=${limit}`;
    return this.httpClient
      .get<AuditLog[]>(utils.GetMasterDataManagementUrl(path), { observe: 'response' })
      .pipe(catchError(() => of(new HttpResponse<AuditLog[]>({ body: [], status: 200, statusText: 'OK' }))));
  }

  downloadToExcel(request: DownloadRequest): Observable<Blob> {
    const url = utils.GetDownloadUrl('repair-location');
    return this.httpClient.post(url, request, { responseType: 'blob' });
  }

  private repairLocationFilterFormValue: RepairLocationFilterFormValue | null = null;

  setRepairLocationFilterFormValue(value: RepairLocationFilterFormValue | null): void {
    this.repairLocationFilterFormValue = value;
  }

  getRepairLocationFilterFormValue(): RepairLocationFilterFormValue | null {
    return this.repairLocationFilterFormValue;
  }
}
